// src/pages/feedback/FeedbackCreatePage.tsx
//
// 작성/수정 겸용 단일 페이지 폼. `/feedback/create` 와 `/feedback/edit/:id`
// 가 같은 컴포넌트를 쓴다 — ContainerRentalCreatePage 와 같은 관례다.
//
// 검증은 contracts/feedback/schemas.ts 의 zod 스키마 하나로 끝낸다.
// 길이 상한(제목 120 / 본문 20,000)은 **서버 검증과 같은 값**이며, 서버가
// 400 을 내면 그 메시지를 스낵바로 그대로 보여준다(최종 방어선은 서버).

import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import ConsoleHeader from "../../components/common/ConsoleHeader.tsx";
import { designTokens } from "../../theme.ts";
import { useFeedbackApi } from "../../hooks/useFeedbackApi.ts";
import { FeedbackPostDto } from "../../contracts/feedback/types.ts";
import {
  FEEDBACK_LIMITS,
  FeedbackPostFormValues,
  feedbackPostFormSchema,
} from "../../contracts/feedback/schemas.ts";
import { TYPE_ORDER, typeLabel } from "./components/feedbackVisuals.ts";

const T = designTokens;

const C = {
  border: "#E4E7EA",
  ink: "#171A1E",
  sub: "#6B7178",
  field: "#F9FAFB",
  accentTint: "#FDEDE8",
  accentHover: "#C93A14",
  neutral: "#F2F4F5",
};

// 유형은 항상 하나가 선택되어 있다(ToggleButtonGroup 의 선택 해제는 무시).
// 사내 게시판에서 가장 잦은 첫 선택이 "문제점"이고, 라벨이 눈에 보이므로
// 잘못 분류돼도 즉시 알아채고 바꿀 수 있다.
const DEFAULT_TYPE = "bug" as const;

export default function FeedbackCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const { user } = useAuth0();

  const isEdit = Boolean(id);
  const inDashboard = location.pathname.startsWith("/dashboard");
  const basePath = inDashboard ? "/dashboard/feedback" : "/feedback";

  const { getPost, createPost, updatePost } = useFeedbackApi();

  const { control, handleSubmit, reset, watch, formState } =
    useForm<FeedbackPostFormValues>({
      // @hookform/resolvers 5 + zod 4 의 타입 마찰 회피 — 기존 폼들과 같은 관례.
      resolver: zodResolver(feedbackPostFormSchema) as any,
      defaultValues: { category: DEFAULT_TYPE, title: "", body: "" },
      mode: "onBlur",
    });

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [existing, setExisting] = useState<FeedbackPostDto | null>(null);
  const [snack, setSnack] = useState<
    { msg: string; severity: "error" | "success" } | null
  >(null);

  const bodyValue = watch("body") ?? "";

  /* ---------- 수정 진입 시 기존 글 로드 ---------- */

  const loadExisting = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const dto = await getPost(id);
      setExisting(dto);
      reset({ category: dto.category, title: dto.title, body: dto.body });
    } catch (e: any) {
      setLoadError(e?.message ?? "글을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isEdit) loadExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id]);

  /* ---------- 이탈 경고 ----------
     hooks/refreshGuard.tsx 는 견적 전용 시그니처(blob setter 요구)라
     재사용할 수 없어 페이지 안에서 직접 처리한다. SPA 내부 이동은
     beforeunload 대상이 아니므로 새로고침·탭 닫기만 막는다. */
  useEffect(() => {
    if (!formState.isDirty || submitting) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [formState.isDirty, submitting]);

  /* ---------- 제출 ---------- */

  const onSubmit = handleSubmit(
    async (values) => {
      setSubmitting(true);
      try {
        // 성공 스낵바는 **도착지에서** 띄운다. 여기서 setSnack 을 하면
        // 같은 tick 의 navigate 로 이 페이지가 즉시 언마운트되어 스낵바가
        // 페인트될 기회가 없다 — 삭제 흐름(FeedbackDetailPage)과 같은
        // location.state.flash 방식으로 넘긴다.
        if (isEdit && id) {
          const updated = await updatePost(id, values);
          navigate(`${basePath}/${updated.id}`, {
            state: { flash: "수정되었습니다." },
          });
        } else {
          const created = await createPost(values);
          navigate(`${basePath}/${created.id}`, {
            state: { flash: "등록되었습니다." },
          });
        }
      } catch (e: any) {
        setSnack({ msg: e?.message ?? "저장하지 못했습니다.", severity: "error" });
      } finally {
        setSubmitting(false);
      }
    },
    (errs) => {
      const labels: Record<string, string> = {
        category: "유형",
        title: "제목",
        body: "내용",
      };
      const fields = Object.keys(errs)
        .map((k) => labels[k] ?? k)
        .join(", ");
      setSnack({ msg: `누락 또는 잘못된 항목: ${fields}`, severity: "error" });
    }
  );

  /* ---------- 렌더 ---------- */

  const backButton = (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate(basePath)}
      sx={{ mb: 2 }}
    >
      목록으로
    </Button>
  );

  const wrapperSx = {
    fontFamily: T.fonts.sans,
    p: inDashboard ? 0 : { xs: 2, md: 3 },
  } as const;

  if (loading) {
    return (
      <Box sx={wrapperSx}>
        {backButton}
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={wrapperSx}>
        {backButton}
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadExisting}>
              다시 시도
            </Button>
          }
        >
          {loadError}
        </Alert>
      </Box>
    );
  }

  // 작성자가 아닌 사용자가 /edit/:id 로 직접 들어온 경우.
  // 프론트 판정은 UI 힌트이고 서버 403 이 최종 방어선이다.
  const notMine =
    isEdit && existing && (!user?.sub || existing.authorSub !== user.sub);

  if (notMine) {
    return (
      <Box sx={wrapperSx}>
        {backButton}
        <Alert severity="warning" sx={{ mb: 2 }}>
          작성자만 수정할 수 있습니다.
        </Alert>
        <Button variant="outlined" onClick={() => navigate(`${basePath}/${id}`)}>
          글로 돌아가기
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={wrapperSx}>
      {backButton}

      <ConsoleHeader
        code={isEdit ? "MARS · FEEDBACK · EDIT" : "MARS · FEEDBACK · NEW"}
        title={isEdit ? "피드백 수정" : "피드백 작성"}
        meta={<>문제점 · 개선점을 팀에 공유합니다</>}
      />

      <Card>
        <CardContent>
          <Box component="form" onSubmit={onSubmit} noValidate>
            {/* 유형 */}
            <FieldBlock label="유형" required>
              <Controller
                name="category"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <ToggleButtonGroup
                      value={field.value}
                      exclusive
                      size="small"
                      onChange={(_e, next) => {
                        if (next === null) return; // 선택 해제 무시 — 항상 하나 선택
                        field.onChange(next);
                      }}
                      aria-label="피드백 유형"
                      sx={{
                        flexWrap: "wrap",
                        gap: 0.6,
                        "& .MuiToggleButton-root": {
                          border: `1px solid ${C.border}`,
                          borderRadius: "8px",
                          bgcolor: "#FFFFFF",
                          fontFamily: T.fonts.sans,
                          fontWeight: 500,
                          fontSize: 13,
                          px: 1.75,
                          py: 0.6,
                          color: C.ink,
                          textTransform: "none",
                          letterSpacing: 0,
                          "&:hover": { bgcolor: C.neutral },
                          "&.Mui-selected": {
                            bgcolor: C.accentTint,
                            borderColor: C.accentTint,
                            color: C.accentHover,
                            fontWeight: 600,
                            "&:hover": { bgcolor: C.accentTint },
                          },
                        },
                        "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
                          marginLeft: 0,
                          borderLeft: `1px solid ${C.border}`,
                          "&.Mui-selected": { borderLeftColor: C.accentTint },
                        },
                      }}
                    >
                      {TYPE_ORDER.map((t) => (
                        <ToggleButton key={t} value={t}>
                          {typeLabel(t)}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    {fieldState.error && (
                      <Typography
                        sx={{
                          mt: 0.75,
                          fontFamily: T.fonts.sans,
                          fontSize: 12,
                          color: "#B12B2B",
                        }}
                      >
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </FieldBlock>

            {/* 제목 */}
            <FieldBlock label="제목" required>
              <Controller
                name="title"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    placeholder="무엇에 대한 이야기인지 한 줄로 적어 주세요"
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ??
                      `${(field.value ?? "").length.toLocaleString("ko-KR")} / ${FEEDBACK_LIMITS.title.toLocaleString("ko-KR")}`
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": { bgcolor: C.field },
                      "& .MuiFormHelperText-root": {
                        textAlign: fieldState.error ? "left" : "right",
                        mx: 0,
                      },
                    }}
                  />
                )}
              />
            </FieldBlock>

            {/* 내용 */}
            <FieldBlock label="내용" required>
              <Controller
                name="body"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={10}
                    maxRows={24}
                    placeholder={
                      "재현 방법, 기대한 동작, 실제 동작을 적어 주시면 좋습니다.\n에러 메시지나 URL 을 그대로 붙여 넣어도 됩니다."
                    }
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ??
                      `${bodyValue.length.toLocaleString("ko-KR")} / ${FEEDBACK_LIMITS.body.toLocaleString("ko-KR")}`
                    }
                    sx={{
                      // 로그 붙여넣기가 잦지만 mono 는 "숫자·금액 전용"이라는
                      // theme.ts 규칙을 지켜 본문 서체를 그대로 쓴다.
                      "& .MuiInputBase-root": { p: 0, bgcolor: C.field },
                      "& textarea": {
                        p: "12px",
                        fontFamily: T.fonts.sans,
                        fontSize: 14.5,
                        lineHeight: 1.7,
                      },
                      "& .MuiFormHelperText-root": {
                        textAlign: fieldState.error ? "left" : "right",
                        mx: 0,
                      },
                    }}
                  />
                )}
              />
            </FieldBlock>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
                flexDirection: { xs: "column-reverse", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate(isEdit && id ? `${basePath}/${id}` : basePath)}
                disabled={submitting}
              >
                취소
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={
                  submitting ? <CircularProgress size={16} color="inherit" /> : undefined
                }
              >
                {isEdit ? "수정하기" : "등록하기"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            variant="filled"
            onClose={() => setSnack(null)}
            sx={{ width: "100%", borderRadius: "8px", fontFamily: T.fonts.sans }}
          >
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

/* ------------------------------------------------------------------ */

function FieldBlock({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        component="div"
        sx={{
          mb: 0.75,
          fontFamily: T.fonts.sans,
          fontSize: 13,
          fontWeight: 600,
          color: C.sub,
        }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: "#E2451C", ml: 0.25 }}>
            *
          </Box>
        )}
      </Typography>
      {children}
    </Box>
  );
}
